/* ============================================================================
   Wolden — Free-shipping discount auto-apply
   ----------------------------------------------------------------------------
   Goal: guarantee the promoted free-shipping promise is actually delivered.
   The store advertises free shipping, but a manual discount code only takes
   effect if it is in the cart/session by the time the customer checks out.
   Nothing was auto-applying it, so customers who didn't manually type the code
   paid shipping (confirmed: 2 orders shipped without free shipping).

   Mechanism (researched 2026-05-19, Shopify docs + Summer Editions '25):
     fetch('/discount/<CODE>') is Shopify's native discount-link endpoint. A
     same-origin fetch to it (redirect:'manual' so we don't download the
     homepage HTML the 302 points at):
       1) sets the `discount_code` cookie  → carries to EVERY checkout entry
          point (cart drawer "Checkout", /cart "Checkout", AND Shop Pay /
          accelerated wallets, because Shopify's checkout reads the cookie
          regardless of which button started the session), and
       2) attaches the code to cart.discount_codes (applicable:true) for cart
          state consistency.
     Verified live: after the fetch, cart.js shows
       discount_codes:[{code:"FALLFREESHIP",applicable:true}]
     and the code is present in the generated /checkouts/cn/... document.

   Free-shipping discounts don't reduce the cart subtotal (they zero shipping
   at the shipping step), so there is intentionally no visible cart-total
   change — the effect appears at checkout's shipping calculation.

   Idempotent + defensive: skips when the code is already on the cart, never
   throws into the page, and preserves any other discount codes the customer
   may have entered (comma-merge).
   ========================================================================== */
(function () {
  'use strict';

  var CODE = 'FALLFREESHIP';
  var _inFlight = false;

  // ---- helpers -------------------------------------------------------------

  function codeMatches(a, b) {
    return String(a || '').trim().toUpperCase() === String(b || '').trim().toUpperCase();
  }

  // Is our code already attached to the cart? (cart.discount_codes is the
  // authoritative source — the cookie can lag or be cleared.)
  function cartHasOurCode(cart) {
    var codes = (cart && cart.discount_codes) || [];
    for (var i = 0; i < codes.length; i++) {
      if (codes[i] && codeMatches(codes[i].code, CODE)) return true;
    }
    return false;
  }

  // Build the discount string to apply: our code + any other codes already on
  // the cart (deduped), so we never clobber a code the customer entered.
  function buildDiscountParam(cart) {
    var out = [CODE];
    var seen = {};
    seen[CODE.toUpperCase()] = true;
    var existing = (cart && cart.discount_codes) || [];
    for (var i = 0; i < existing.length; i++) {
      var c = existing[i] && existing[i].code;
      if (c && !seen[c.toUpperCase()]) {
        seen[c.toUpperCase()] = true;
        out.push(c);
      }
    }
    return out.join(',');
  }

  function getCart() {
    return fetch('/cart.js', { credentials: 'same-origin', headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .catch(function () { return null; });
  }

  // The native discount-link endpoint. Sets the cookie + attaches to cart.
  // redirect:'manual' avoids following the 302 to the homepage (the Set-Cookie
  // header is still honored for same-origin requests).
  function applyViaDiscountLink(param) {
    return fetch('/discount/' + encodeURIComponent(param), {
      method: 'GET',
      redirect: 'manual',
      credentials: 'same-origin'
    }).catch(function () { /* non-fatal */ });
  }

  // Secondary path (Summer Editions '25): attach to cart via the AJAX cart
  // API. Belt-and-suspenders so cart.discount_codes is definitely populated
  // even if the discount-link cookie write is blocked by a privacy setting.
  function applyViaCartUpdate(param) {
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'application/json'
      },
      body: JSON.stringify({ discount: param })
    }).catch(function () { /* non-fatal */ });
  }

  // ---- main ----------------------------------------------------------------

  // Ensure the free-shipping code is applied. Idempotent: no-op when already
  // present. Safe to call repeatedly (page load, cart events, checkout intent).
  function ensure() {
    if (_inFlight) return Promise.resolve();
    _inFlight = true;
    return getCart().then(function (cart) {
      // Empty cart → nothing to discount yet. The cookie persists from a later
      // call once items exist; we still set it cheaply below so it's ready.
      if (!cart) { _inFlight = false; return; }
      if (cartHasOurCode(cart)) { _inFlight = false; return; }

      var param = buildDiscountParam(cart);
      // Discount-link first (sets cookie → universal carry incl. Shop Pay),
      // then cart-update as a second layer for cart-state consistency.
      return applyViaDiscountLink(param)
        .then(function () { return applyViaCartUpdate(param); })
        .then(function () {
          _inFlight = false;
          try { document.dispatchEvent(new CustomEvent('wolden:free-shipping-applied', { detail: { code: CODE } })); } catch (e) {}
        })
        .catch(function () { _inFlight = false; });
    }).catch(function () { _inFlight = false; });
  }

  // 1) On page load (cart persists across navigations, so this catches the
  //    common "added earlier, came back to check out" path).
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', ensure, { once: true });
  } else {
    ensure();
  }

  // 2) After cart mutations. Horizon dispatches a CartUpdateEvent on the
  //    document; we also listen to a few legacy/alternate names for safety.
  ['cart:updated', 'cart:refresh', 'cart:change', 'CartUpdateEvent', 'cart:build'].forEach(function (evt) {
    document.addEventListener(evt, function () { setTimeout(ensure, 150); });
  });

  // 3) Checkout intent — widen the lead time so the discount is definitely on
  //    the session before the checkout navigation fires. Non-blocking; the
  //    page-load apply above is the primary guarantee. We listen on
  //    pointerdown/focus (earlier than click) for any checkout control and for
  //    the cart-drawer trigger (customer is about to convert).
  function onCheckoutIntent(e) {
    var t = e.target;
    if (!t || !t.closest) return;
    var hit = t.closest(
      'button[name="checkout"], [name="checkout"], #checkout, .cart__checkout-button, ' +
      '.cart-drawrer-btn, [href="/cart"], a[href$="/cart"], ' +
      '.additional-checkout-buttons, shopify-accelerated-checkout, ' +
      'shopify-accelerated-checkout-cart, [data-shopify="dynamic-checkout-cart"]'
    );
    if (hit) ensure();
  }
  document.addEventListener('pointerdown', onCheckoutIntent, true);
  document.addEventListener('focusin', onCheckoutIntent, true);

  // Expose for manual testing / diagnostics.
  window.woldenEnsureFreeShipping = ensure;
})();
